import React, { Component } from "react";
import { connect } from "react-redux";
import { withStyles } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TablePagination from "@material-ui/core/TablePagination";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Backdrop from "@material-ui/core/Backdrop";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Checkbox from "@material-ui/core/Checkbox";
import ListItemText from "@material-ui/core/ListItemText";
import Chip from "@material-ui/core/Chip";
import axios from "axios";
import moment from "moment";
import { changeHeaderName } from "../../actions/dashboardActions";
import { getAuditLogs } from "../../actions/auditLogActions";

const styles = (theme) => ({
    container: {
        paddingTop: theme.spacing(3),
        paddingBottom: theme.spacing(3),
    },
    header: {
        fontWeight: 700,
        marginBottom: theme.spacing(2),
    },
    filterPaper: {
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    filterRow: {
        display: "flex",
        flexWrap: "wrap",
        gap: theme.spacing(2),
        alignItems: "flex-end",
    },
    filterField: {
        minWidth: 180,
    },
    businessSelect: {
        minWidth: 240,
        maxWidth: 400,
    },
    chips: {
        display: "flex",
        flexWrap: "wrap",
        gap: 4,
    },
    chip: {
        height: 22,
        fontSize: "0.75rem",
    },
    filterActions: {
        display: "flex",
        justifyContent: "flex-end",
        gap: theme.spacing(1),
        marginTop: theme.spacing(2),
    },
    resetBtn: {
        height: 36,
    },
    tablePaper: {
        width: "100%",
        overflow: "hidden",
    },
    tableContainer: {
        maxHeight: 560,
    },
    tableHead: {
        backgroundColor: "#14B7DD",
    },
    tableHeadCell: {
        color: "#fff",
        fontWeight: 700,
        whiteSpace: "nowrap",
    },
    activityCell: {
        maxWidth: 420,
        wordBreak: "break-word",
    },
    timeAgo: {
        whiteSpace: "nowrap",
        color: "#888",
        fontSize: "0.82rem",
    },
    noData: {
        textAlign: "center",
        padding: theme.spacing(4),
        color: "#888",
    },
    backdrop: {
        zIndex: theme.zIndex.drawer + 1,
        color: "#fff",
    },
});

class AuditLog extends Component {
    constructor(props) {
        super(props);
        this.state = {
            page: 0,
            rowsPerPage: 10,
            search: "",
            selectedBusinesses: [],
            allBusinesses: [],
            startDate: "",
            endDate: "",
            loading: false,
        };
    }

    componentDidMount() {
        this.props.changeHeaderName("Audit Log");
        this.loadBusinesses();
        this.fetchLogs();
    }

    loadBusinesses = async () => {
        try {
            const response = await axios.get("/api/business/getApprovedBusinesses");
            const data = (response.data && response.data.data) ? response.data.data : [];
            const sorted = data
                .filter(b => b.businessId && b.businessName)
                .sort((a, b) => a.businessName.localeCompare(b.businessName));
            this.setState({ allBusinesses: sorted });
        } catch (err) {
            console.error("Failed to load businesses for filter:", err);
        }
    };

    fetchLogs = () => {
        const { page, rowsPerPage, search, selectedBusinesses, allBusinesses, startDate, endDate } = this.state;
        this.setState({ loading: true });
        this.props
            .getAuditLogs({
                page: page + 1,
                limit: rowsPerPage,
                search: search || undefined,
                businesses: selectedBusinesses.length > 0
                    ? selectedBusinesses
                        .map((bid) => {
                            const b = allBusinesses.find((x) => x.businessId === bid);
                            return b ? b.businessName : null;
                        })
                        .filter(Boolean)
                    : undefined,
                startDate: startDate || undefined,
                endDate: endDate || undefined,
            })
            .finally(() => this.setState({ loading: false }));
    };

    handleSearch = () => {
        this.setState({ page: 0 }, this.fetchLogs);
    };

    handleReset = () => {
        this.setState(
            { page: 0, search: "", selectedBusinesses: [], startDate: "", endDate: "" },
            this.fetchLogs
        );
    };

    handleChangePage = (event, newPage) => {
        this.setState({ page: newPage }, this.fetchLogs);
    };

    handleChangeRowsPerPage = (event) => {
        this.setState(
            { rowsPerPage: parseInt(event.target.value, 10), page: 0 },
            this.fetchLogs
        );
    };

    handleKeyDown = (e) => {
        if (e.key === "Enter") this.handleSearch();
    };

    render() {
        const { classes, auditLog } = this.props;
        const { search, selectedBusinesses, allBusinesses, startDate, endDate, loading, page, rowsPerPage } = this.state;
        const { data = [], total = 0 } = auditLog || {};

        return (
            <Container maxWidth="xl" className={classes.container}>
                <Backdrop className={classes.backdrop} open={loading}>
                    <CircularProgress color="inherit" />
                </Backdrop>

                <Typography variant="h5" className={classes.header}>
                    Audit Log
                </Typography>

                {/* Filters */}
                <Paper className={classes.filterPaper} elevation={2}>
                    <div className={classes.filterRow}>
                        <TextField
                            label="Search by User Name"
                            variant="outlined"
                            size="small"
                            className={classes.filterField}
                            value={search}
                            onChange={(e) => this.setState({ search: e.target.value })}
                            onKeyDown={this.handleKeyDown}
                        />
                        <FormControl variant="outlined" size="small" className={classes.businessSelect}>
                            <InputLabel id="business-filter-label">Filter by Business</InputLabel>
                            <Select
                                labelId="business-filter-label"
                                multiple
                                value={selectedBusinesses}
                                onChange={(e) => this.setState({ selectedBusinesses: e.target.value })}
                                label="Filter by Business"
                                renderValue={(selected) => (
                                    <div className={classes.chips}>
                                        {selected.map((bizId) => {
                                            const biz = allBusinesses.find(b => b.businessId === bizId);
                                            return (
                                                <Chip
                                                    key={bizId}
                                                    label={biz ? biz.businessName : bizId}
                                                    className={classes.chip}
                                                    size="small"
                                                />
                                            );
                                        })}
                                    </div>
                                )}
                                MenuProps={{ PaperProps: { style: { maxHeight: 300, width: 320 } } }}
                            >
                                {allBusinesses.map((biz) => (
                                    <MenuItem key={biz.businessId} value={biz.businessId}>
                                        <Checkbox
                                            checked={selectedBusinesses.indexOf(biz.businessId) > -1}
                                            color="primary"
                                            size="small"
                                        />
                                        <ListItemText
                                            primary={biz.businessName + " (" + biz.businessId + ")"}
                                        />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            label="From Date"
                            type="date"
                            variant="outlined"
                            size="small"
                            className={classes.filterField}
                            value={startDate}
                            onChange={(e) => this.setState({ startDate: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            label="To Date"
                            type="date"
                            variant="outlined"
                            size="small"
                            className={classes.filterField}
                            value={endDate}
                            onChange={(e) => this.setState({ endDate: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                        />
                    </div>
                    <div className={classes.filterActions}>
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={this.handleSearch}
                            className={classes.resetBtn}
                        >
                            Search
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={this.handleReset}
                            className={classes.resetBtn}
                        >
                            Reset
                        </Button>
                    </div>
                </Paper>

                {/* Total count */}
                <Typography variant="body2" style={{ marginBottom: 8, color: "#555" }}>
                    Total Records: <strong>{total}</strong>
                </Typography>

                {/* Table */}
                <Paper className={classes.tablePaper} elevation={2}>
                    <TableContainer className={classes.tableContainer}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    {["Sr No", "User Name", "Business Id", "Activity", "Date & Time", "Time Ago"].map(
                                        (col) => (
                                            <TableCell
                                                key={col}
                                                className={classes.tableHeadCell}
                                                style={{ backgroundColor: "#14B7DD" }}
                                            >
                                                {col}
                                            </TableCell>
                                        )
                                    )}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} className={classes.noData}>
                                            No audit logs found.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    data.map((log, index) => (
                                        <TableRow key={log._id} hover>
                                            <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                                            <TableCell>{log.userName || "Unknown"}</TableCell>
                                            <TableCell>{log.businessId || "-"}</TableCell>
                                            <TableCell className={classes.activityCell}>
                                                {log.activity}
                                            </TableCell>
                                            <TableCell style={{ whiteSpace: "nowrap" }}>
                                                {moment(log.createdAt).format("DD MMM YYYY hh:mm A")}
                                            </TableCell>
                                            <TableCell className={classes.timeAgo}>
                                                {moment(log.createdAt).fromNow()}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[10, 25, 50]}
                        component="div"
                        count={total}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onChangePage={this.handleChangePage}
                        onChangeRowsPerPage={this.handleChangeRowsPerPage}
                    />
                </Paper>
            </Container>
        );
    }
}

const mapStateToProps = (state) => ({
    auditLog: state.auditLog,
});

export default connect(mapStateToProps, { changeHeaderName, getAuditLogs })(
    withStyles(styles)(AuditLog)
);
